import { gql } from '@apollo/client';

/**
 * Inline GraphQL operations for the (hidden) Zoho Deal quote builder.
 *
 * These are written as inline `gql` documents + hand-typed result/variable
 * types on purpose: the frontend codegen (`generate-types`) introspects the
 * PROD GraphQL endpoint, so it can only emit generated hooks for these once the
 * backend `getZohoDealQuoteMeta` / `createZohoQuoteForDeal` schema is deployed.
 * Until then this keeps the page fully typed and functional. After deploy, this
 * can be migrated to co-located `.graphql` files + generated hooks.
 */

export const GET_ZOHO_DEAL_QUOTE_META = gql`
  query GetZohoDealQuoteMeta($dealId: String!) {
    getZohoDealQuoteMeta(dealId: $dealId) {
      dealId
      dealName
      entity
      currency
      defaultLanguage
      mainContractor {
        accountId
        name
        billingCountryCode
      }
      availableTaxes {
        taxId
        taxName
        taxPercentage
      }
      suggestedTaxId
      containerTypes {
        id
        name
        group
        hexCode
        imageUrl
        iconUrl
        packingUnit
        defaultRentPrice
        defaultCleaningPrice
        defaultLossPrice
        defaultClearingPrice
      }
    }
  }
`;

export const CREATE_ZOHO_QUOTE_FOR_DEAL = gql`
  mutation CreateZohoQuoteForDeal($input: CreateZohoQuoteInput!) {
    createZohoQuoteForDeal(input: $input) {
      estimateId
      estimateNumber
      estimateUrl
    }
  }
`;

export interface QuoteTax {
  taxId: string;
  taxName: string | null;
  taxPercentage: number | null;
}

export interface QuoteContainerType {
  id: number;
  name: string;
  group: string;
  hexCode: string | null;
  imageUrl: string | null;
  iconUrl: string | null;
  packingUnit: number;
  defaultRentPrice: number; // cents
  defaultCleaningPrice: number; // cents
  defaultLossPrice: number; // cents
  defaultClearingPrice: number; // cents
}

export interface ZohoDealQuoteMeta {
  dealId: string;
  dealName: string | null;
  entity: string;
  currency: string;
  defaultLanguage: string;
  mainContractor: {
    accountId: string;
    name: string | null;
    billingCountryCode: string | null;
  } | null;
  availableTaxes: QuoteTax[];
  suggestedTaxId: string | null;
  containerTypes: QuoteContainerType[];
}

export interface GetZohoDealQuoteMetaData {
  getZohoDealQuoteMeta: ZohoDealQuoteMeta;
}

export interface GetZohoDealQuoteMetaVars {
  dealId: string;
}

export interface CreateZohoQuoteLineItemInput {
  containerTypeId: number;
  boxAmount: number;
  rentPrice: number; // cents
  cleaningPrice: number; // cents
  lossPrice: number; // cents
  clearingPrice: number; // cents
}

export interface CreateZohoQuoteInput {
  dealId: string;
  language: string;
  taxId?: string | null;
  includeRent: boolean;
  includeCleaning: boolean;
  includeLoss: boolean;
  includeClearing: boolean;
  lineItems: CreateZohoQuoteLineItemInput[];
}

export interface CreateZohoQuoteForDealData {
  createZohoQuoteForDeal: {
    estimateId: string;
    estimateNumber: string | null;
    estimateUrl: string;
  };
}

export interface CreateZohoQuoteForDealVars {
  input: CreateZohoQuoteInput;
}
